import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useProgressiveNerv } from '@/hooks/useProgressiveNerv';
import DailyNervGraph from './DailyNervGraph';
import NervInventoryUpload from './NervInventoryUpload';
import NervComparisonView from './NervComparisonView';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, subMonths, subDays } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import { useSyncCompletionCheck } from '@/hooks/useSyncCompletionCheck';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';

interface WhatIfInventoryAnalysisProps {
  organisation: any;
}

interface UploadedInventory {
  sku: string;
  quantity: number;
}

const ALLOWED_ADMIN_ID = '4e1f1741-6091-4a4b-b1c2-f0640f34430c';

const WhatIfInventoryAnalysis: React.FC<WhatIfInventoryAnalysisProps> = ({ organisation }) => {
  const { user } = useAuth();
  const isAllowedAdmin = user?.id === ALLOWED_ADMIN_ID;

  // Initialize from org config if available
  const defaultSource = React.useMemo(() => {
    const config = organisation?.inventory_source_config;
    if (!config?.primary_source || config.primary_source === 'all') return 'All';
    // Map config values to display values
    const sourceMap: Record<string, 'All' | 'Shopify' | 'Amazon'> = {
      'shopify': 'Shopify',
      'amazon': 'Amazon',
      'shopify_csv': 'Shopify',
      'amazon_csv': 'Amazon',
    };
    return sourceMap[config.primary_source] || 'All';
  }, [organisation?.inventory_source_config]);

  const [selectedSource, setSelectedSource] = useState<'All' | 'Shopify' | 'Amazon'>(defaultSource);
  const [customStartDate, setCustomStartDate] = useState<Date>(subMonths(new Date(), 1));

  // Check if all Fivetran syncs have completed for today
  const { latestSyncedDate, allSynced, loading: syncCheckLoading } = useSyncCompletionCheck(organisation);
  const [customEndDate, setCustomEndDate] = useState<Date>(subDays(new Date(), 1));

  // Adjust end date based on sync status
  React.useEffect(() => {
    if (!syncCheckLoading) {
      setCustomEndDate(latestSyncedDate);
    }
  }, [latestSyncedDate, syncCheckLoading]);
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
  const [uploadedInventory, setUploadedInventory] = useState<UploadedInventory[] | null>(null);
  const [viewMode, setViewMode] = useState<'current' | 'realistic' | 'potential' | 'combined'>('current');

  // Update when config changes
  React.useEffect(() => {
    setSelectedSource(defaultSource);
  }, [defaultSource]);

  const { data: nervData, isLoading, error } = useProgressiveNerv({
    organisation,
    sources: selectedSource === 'All' ? ['Shopify', 'Amazon'] : [selectedSource],
    dateRange: {
      startDate: customStartDate,
      endDate: customEndDate
    },
    selectedSkus: selectedSkus.length > 0 ? selectedSkus : [],
    uploadedInventory: uploadedInventory || undefined,
  });

  const handleUpload = (inventory: UploadedInventory[]) => {
    setUploadedInventory(inventory);
    setViewMode('combined');
    toast.success(`Uploaded ${inventory.length} Products for analysis`);
  };

  const handleClear = () => {
    setUploadedInventory(null);
    setViewMode('current');
  };

  // Calculate totals from latest date in aggregated data
  const latestData = nervData[nervData.length - 1];
  const currentTotal = latestData?.current || 0;
  const realisticTotal = latestData?.uploadedRealistic || 0;
  const potentialTotal = latestData?.uploadedPotential || 0;

  if (!isAllowedAdmin) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <NervInventoryUpload
            onUpload={handleUpload}
            onClear={handleClear}
            uploadedData={uploadedInventory}
      />

      {/* Comparison View - Only show when data is uploaded */}
      {/* {uploadedInventory && (
        <NervComparisonView
          currentNerv={currentTotal}
          realisticNerv={realisticTotal}
          potentialNerv={potentialTotal}
        />
      )} */}

      {/* Controls and Graph */}
      <Card>
        <CardHeader>
          <CardTitle>NERV Analysis</CardTitle>
          <CardDescription>
            {uploadedInventory 
              ? 'Compare current inventory NERV with projected scenarios. Avg price uses all sources, inventory from uploaded data.'
              : 'View current inventory NERV trends. Avg price uses all sources, inventory from selected source.'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Sync pending banner */}
          {!syncCheckLoading && !allSynced && (
            <Alert variant="default" className="border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                Waiting for today's sync to complete — showing data through {format(latestSyncedDate, 'MMM d, yyyy')}
              </AlertDescription>
            </Alert>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            {/* Source Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Inventory Source</label>
              <Select value={selectedSource} onValueChange={(value: any) => setSelectedSource(value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Sources</SelectItem>
                  <SelectItem value="Shopify">Shopify</SelectItem>
                  <SelectItem value="Amazon">Amazon</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Avg price uses all sources</p>
            </div>

            {/* Date Range */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Start Date</label>
              <input
                type="date"
                value={format(customStartDate, 'yyyy-MM-dd')}
                onChange={(e) => setCustomStartDate(new Date(e.target.value))}
                className="px-3 py-2 border rounded-md"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">End Date</label>
              <input
                type="date"
                value={format(customEndDate, 'yyyy-MM-dd')}
                onChange={(e) => setCustomEndDate(new Date(e.target.value))}
                className="px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          {/* View Mode Tabs - Only show when uploaded data exists */}
          {uploadedInventory && (
            <Tabs value={viewMode} onValueChange={(v: any) => setViewMode(v)}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="current">Current</TabsTrigger>
                <TabsTrigger value="realistic">Realistic</TabsTrigger>
                <TabsTrigger value="potential">Potential</TabsTrigger>
                <TabsTrigger value="combined">Compare All</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

      {/* Graph */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-destructive">
              Error loading data: {error}
            </div>
          ) : nervData.length > 0 ? (
          <DailyNervGraph 
            data={nervData}
            showComparison={!!uploadedInventory}
            viewMode={viewMode}
          />
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              No NERV data available for the selected filters
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatIfInventoryAnalysis;
